define('bindingHandlers/durationPicker',[
    'lib/knockout',
    'lib/lodash4',
    'moment',
    'bindingHandlers/util/spinnerUtil',
    'extensions/knockoutExtenders/numeric',
    'bindingHandlers/include'
], function (ko, _, moment, spinnerUtil) {
    "use strict";

    /**
     * Duration Picker
     *
     * @description: creates a multi-input widget that allows users to enter hours,
     *      minutes, and seconds (configurable) and binds as miliseconds.
     *
     * REQUIRES:
     *
     * value (observable) - value for milliseconds
     *
     * OPTIONAL:
     *
     * template (string) - a path to the view to use as the template (see: duration_hms.html, duration_ms.html)
     *     the default template is duration_hms.html if no other is specified
     *
     */

    ko.bindingHandlers.durationPicker = {
        init: function(element, valueAccessor, allBindings, viewModel, bindingContext) {
            var self = this;
            var duration_template = 'templates/widgets/durationPicker/duration_hms';
            var milliseconds = null;
            var now = moment();
            var units = ["year", "month", "day", "hour", "minute", "second"];
            var bindingData = valueAccessor();
            // create variable to hold the duration picker template
            var templateData = {};
            var tempHolder = {};
            var timeUnits;
            var step = {
                year: 1,
                month: 1,
                day: 1,
                hour: 1,
                minute: 1,
                second: 1
            };
            var roundToNearest = {
                year: 0,
                month: 0,
                day: 0,
                hour: 0,
                minute: 0,
                second: 0
            };
            var stepRoundsValue = {};
            var unitMax =  {
                year: ko.observable(100),
                month: ko.observable(365),
                day: ko.observable(900000),
                hour: ko.observable(1000),
                minute: ko.observable(1000),
                second: ko.observable(1000)
            };
            var unitMin =  {
                year: ko.observable(0),
                month: ko.observable(0),
                day: ko.observable(0),
                hour: ko.observable(0),
                minute: ko.observable(0),
                second: ko.observable(0)
            };

            /**
             * validates and sets values
             * @returns {void}
             */
            function validateAndSet(){

                //set milliseconds
                if (ko.isObservable(bindingData)) {
                    milliseconds = bindingData;
                }

                //sort bindingData.units based on units array above, if there are any units defined
                if (bindingData.units){
                    if (!validateUnits(bindingData.units, units)){
                        throw new Error("DurationPicker binding Error. Unit not recognized as a valid unit");
                    }
                    timeUnits = sortBasedOnArray(bindingData.units, units);
                } else if (bindingData.unit){
                    var unit = {
                        hms: ["hour", "minute", "second"],
                        ymd: ["year", "month", "day"],
                        all: ["year", "month", "day", "hour", "minute", "day"]
                    };
                    timeUnits = unit[bindingData.unit];
                } else {
                    timeUnits = ["hour", "minute", "second"];
                }

                //if there is a value for binding data, set milliseconds to the value
                if (bindingData.value && ko.isObservable(bindingData.value)) {
                    milliseconds = bindingData.value;
                }

                //if there is a template, set it
                if (bindingData.template && typeof(bindingData.template) === 'string') {
                    duration_template = bindingData.template;
                }

                //check if milliseconds is null
                if (milliseconds === null) {
                    throw new Error("DurationPicker binding error. Must specify as follows: data-bind='durationPicker: observable' OR data-bind='durationPicker:{value: observable, template: 'path to template'}'");
                }

                if (bindingData.range) {
                    _.each(timeUnits, function(unit) {
                        if (bindingData.range[unit]) {
                            //check if there is a specific min for the unit
                            if (bindingData.range[unit].max) {
                                var max = bindingData.range[unit].max;

                                if (_.isInteger(max)) {
                                    unitMax[unit](max);
                                } else if (ko.isObservable(max)) {
                                    unitMax[unit] = max;
                                }
                            }

                            //check if there is a specific max for the unit
                            if (bindingData.range[unit].min) {
                                var min = bindingData.range[unit].min;

                                if (_.isInteger(min)) {
                                    unitMin[unit](min);
                                } else if (ko.isObservable(min)) {
                                    unitMin[unit] = min;
                                }
                            }

                            //check if there is a specific roundToNearest value for the unit
                            if (bindingData.range[unit].roundToNearest && _.isInteger(bindingData.range[unit].roundToNearest) && bindingData.range[unit].roundToNearest !== 0) {
                                roundToNearest[unit] = bindingData.range[unit].roundToNearest;
                                step[unit] = bindingData.range[unit].roundToNearest;
                            }

                            //check if there is a specific step for the unit
                            if (bindingData.range[unit].step && _.isInteger(bindingData.range[unit].step)) {
                                step[unit] = bindingData.range[unit].step;
                            }

                            if (bindingData.range[unit].stepRoundsValue) {
                                stepRoundsValue[unit] = true;
                            }
                        }
                    });
                }

                /**
                 * @param incomingArray : String[]
                 * @param finalArray : String[]
                 * @returns {String[]}
                 */
                function sortBasedOnArray(incomingArray, finalArray){
                    var arrayToBeReturned = [];
                    _.each(incomingArray, function(item){
                        var unitObject = {};
                        unitObject.name = item;
                        unitObject.value = finalArray.indexOf(item);
                        arrayToBeReturned.push(unitObject);
                    });
                    return _.map(_.sortBy(arrayToBeReturned, 'value'), 'name');
                }

                /**
                 * @param incomingArray : String[]
                 * @param finalArray : String[]
                 * @returns {boolean}
                 */
                function validateUnits(incomingArray, finalArray){
                    _.each(incomingArray, function(unit){
                        if (!_.includes(finalArray, unit)){
                            return false;
                        }
                    });

                    return true;
                }
            }

            /**
             * converts milliseconds to desired units
             * @param milliseconds : int
             * @returns {void}
             */
            var millisecondsToUnits = function(milliseconds){

                //sets the total duration in milliseconds
                var millisecondDuration = moment.duration(milliseconds, 'milliseconds');

                _.each(timeUnits, function(unit){
                    //dynamically sets a lo-dash function name to the correct unit
                    var functionName = "as" + unit.charAt(0).toUpperCase() + unit.slice(1) + "s";
                    var unitCount = Math.floor(millisecondDuration[functionName]());
                    millisecondDuration = millisecondDuration.subtract(unitCount, unit + "s");
                    tempHolder[unit] = ko.observable(unitCount).extend({numeric: {precision: 0, min: unitMin[unit], max: unitMax[unit]}});
                });
            };

            validateAndSet();
            millisecondsToUnits(milliseconds());

            /**
             * converts the units back to milliseconds
             * @returns {int}
             */
            var unitsToMilliseconds = function(){
                var future = moment(now);
                _.each(tempHolder, function(unit, key){
                    future.add(unit(), key);
                });
                var difference = moment(future.diff(now));
                return difference._i;
            };

            templateData.durationPickerId = _.uniqueId("duration-picker-");

            templateData.totalMilliseconds = ko.computed(function(){
                return (unitsToMilliseconds());
            });

            templateData.totalMilliseconds.subscribe(function(newValue) {
                if (milliseconds() != newValue) {
                    milliseconds(newValue);
                }
            });

            milliseconds.subscribe(function(newValue) {
                // don't allow negative durations
                if (newValue < 0) {
                    milliseconds(0);
                    return;
                }

                var millisecondDuration = moment.duration(milliseconds(), 'milliseconds');

                _.each(timeUnits, function(unit) {
                    var functionName = "as" + unit.charAt(0).toUpperCase() + unit.slice(1) + "s";
                    var unitCount = Math.floor(millisecondDuration[functionName]());

                    if (roundToNearest[unit] !== 0) {
                        var rounded = roundToNearest[unit] * Math.round(unitCount / roundToNearest[unit]);
                        unitCount = Math.min(unitMax[unit](), Math.max(unitMin[unit](), rounded));
                    }

                    millisecondDuration = millisecondDuration.subtract(unitCount, unit + "s");
                    tempHolder[unit](unitCount);
                });
            });

            /**
             * this is here so that we are not breaking backwards compatibility, it takes all the values from tempHolder
             * and puts it into templateData
             */
            _.each(tempHolder, function(unit, key){
                templateData[key] = unit;
            });

            // merge the templateData set above with the click handlers from the spinner utility
            _.merge(templateData, spinnerUtil.handleClickDirections(tempHolder, step, undefined, stepRoundsValue));

            var includeValueAccessor = ko.observable({
                name: duration_template,
                data: templateData
            });

            ko.bindingHandlers.include.init.call (self, element, includeValueAccessor, allBindings, viewModel, bindingContext);
            ko.bindingHandlers.include.update.call(self, element, includeValueAccessor, allBindings, viewModel, bindingContext);
        }
    };
});

